import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Row,
  Col,
  Alert,
  Spin,
  Upload,
  Tag,
  Tooltip,
  message,
  Tabs,
  Radio,
  Switch,
  Card,
  Divider,
} from "antd";
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  GlobalOutlined,
  ShopOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ProjectOutlined,
  BookOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  PlusOutlined,
  StarOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  FileDoneOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PagesHeader from "../../components/PageHeader";
import ButtonBar from "../../components/ButtonNavigation";
import {
  getCleaningRestorationProfileData,
  updateCleaningRestorationProfile,
} from "../../services/CleaningRestorationService";
import { getUserData } from "../../services/GetUserData";
import { set_user_auth } from "../../store/actions/user";
import { MEDIA_UPLOAD_URL, WEBSITE_URL } from "../../Helper";
import "./CleaningRestorationDashboard.css";

const { Option } = Select;
const { TabPane } = Tabs;

export const CLEANING_RESTORATION_SERVICES_CATEGORIES = [
  {
    category: "Carpet, Fabric & Textile Care",
    tagline: "Carpet, upholstery, area rugs, leather, specialized dye & reinstallation services",
    iconType: "textile",
    services: [
      { label: "Carpet Cleaning", value: "carpet_cleaning" },
      { label: "Upholstery & Fabric Cleaning", value: "upholstery_cleaning" },
      { label: "Area Rug Cleaning", value: "area_rug_cleaning" },
      { label: "Residential House Cleaning", value: "residential_house_cleaning" },
      { label: "Commercial Carpet Maintenance", value: "commercial_carpet_maintenance" },
      { label: "Leather Cleaning", value: "leather_cleaning" },
      { label: "Carpet Dyeing & Color Repair", value: "carpet_dyeing_color_repair" },
      { label: "Carpet Repair", value: "carpet_repair" },
      { label: "Carpet Reinstallation & Stretching", value: "carpet_reinstallation_stretching" },
    ],
  },
  {
    category: "Hard Surface Floor Care & Maintenance",
    tagline: "Wood, resilient, stone, ceramic tile & commercial floor maintenance",
    iconType: "hardsurface",
    services: [
      { label: "Wood Floor Cleaning & Maintenance", value: "wood_floor_cleaning" },
      { label: "Resilient Floor Cleaning & Maintenance", value: "resilient_floor_cleaning" },
      { label: "Tile & Grout Cleaning", value: "tile_grout_cleaning" },
      { label: "Hard Surface Floor Care", value: "hard_surface_floor_cleaning" },
      { label: "Stone, Masonry & Ceramic Cleaning", value: "stone_cleaning_restoration" },
      { label: "Commercial Floor Care", value: "commercial_floor_care" },
    ],
  },
  {
    category: "Restoration & Specialty Capabilities",
    tagline: "Water, fire, smoke, mold remediation, moisture inspection & specialty cleanup",
    iconType: "restoration",
    services: [
      { label: "Water Damage Restoration", value: "water_damage_restoration" },
      { label: "Commercial Water Damage Drying", value: "commercial_water_drying" },
      { label: "Structural Drying & Dehumidification", value: "structural_drying" },
      { label: "Fire & Smoke Damage Restoration", value: "fire_smoke_restoration" },
      { label: "Smoke & Soot Cleaning", value: "smoke_soot_cleaning" },
      { label: "Mold Remediation", value: "mold_remediation" },
      { label: "Building Moisture Inspection", value: "building_moisture_inspection" },
      { label: "Structural Moisture Assessment", value: "structural_moisture_assessment" },
      { label: "Odor Removal & Deodorization", value: "odor_removal_deodorization" },
      { label: "Contents Cleaning & Restoration", value: "contents_cleaning_restoration" },
      { label: "Trauma & Crime Scene Cleanup", value: "trauma_crime_scene_cleanup" },
      { label: "Air Duct / HVAC Cleaning", value: "air_duct_hvac_cleaning" },
    ],
  },
];

export const CLEANING_RESTORATION_SERVICES_LIST = CLEANING_RESTORATION_SERVICES_CATEGORIES.flatMap(
  (cat) => cat.services
);

export const IICRC_CLEANING_CERTS = [
  { label: "CCT - Carpet Cleaning Technician", value: "iicrc_cct" },
  { label: "CCMT - Commercial Carpet Maintenance Technician", value: "iicrc_ccmt" },
  { label: "UFT - Upholstery and Fabric Cleaning Technician", value: "iicrc_uft" },
  { label: "RCT - Rug Cleaning Technician", value: "iicrc_rct" },
  { label: "FCT - Floor Care Technician", value: "iicrc_fct" },
  { label: "RFMT - Resilient Flooring Maintenance Technician", value: "iicrc_rfmt" },
  { label: "SMT - Stone, Masonry and Ceramic Tile Cleaning Technician", value: "iicrc_smt" },
  { label: "WFMT - Wood Floor Maintenance Technician", value: "iicrc_wfmt" },
  { label: "LCT - Leather Cleaning Technician", value: "iicrc_lct" },
  { label: "HCT - House Cleaning Technician", value: "iicrc_hct" },
  { label: "CRT - Color Repair Technician", value: "iicrc_crt" },
];

export const IICRC_RESTORATION_CERTS = [
  { label: "WRT - Water Damage Restoration Technician", value: "iicrc_wrt" },
  { label: "ASD - Applied Structural Drying Technician", value: "iicrc_asd" },
  { label: "AMRT - Applied Microbial Remediation Technician", value: "iicrc_amrt" },
  { label: "FSRT - Fire and Smoke Damage Restoration Technician", value: "iicrc_fsrt" },
  { label: "OCT - Odor Control Technician", value: "iicrc_oct" },
  { label: "CDS - Commercial Drying Specialist", value: "iicrc_cds" },
  { label: "CPT - Contents Processing Technician", value: "iicrc_cpt" },
  { label: "BMI - Building Moisture Inspection", value: "iicrc_bmi" },
  { label: "HST - Health and Safety Technician", value: "iicrc_hst" },
  { label: "RRT - Carpet Repair and Reinstallation Technician", value: "iicrc_rrt" },
  { label: "TCST - Trauma and Crime Scene Technician", value: "iicrc_tcst" },
  { label: "MRS - Mold Remediation Specialist", value: "iicrc_mrs" },
];

export const IICRC_ADVANCED_DESIGNATIONS = [
  { label: "MTC - Master Textile Cleaner", value: "iicrc_mtc" },
  { label: "MWR - Master Water Restorer", value: "iicrc_mwr" },
  { label: "MSR - Master Fire and Smoke Restorer", value: "iicrc_msr" },
];

export const SERVICE_RADIUS_OPTIONS = [
  { label: "25 Miles", value: "25_miles" },
  { label: "50 Miles", value: "50_miles" },
  { label: "100 Miles", value: "100_miles" },
  { label: "200 Miles", value: "200_miles" },
  { label: "350 Miles", value: "350_miles" },
  { label: "500 Miles", value: "500_miles" },
  { label: "Nationwide", value: "nationwide" },
];

const CleaningRestorationDashboard = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.User?.userDetails || state.user?.userData);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [profilePicId, setProfilePicId] = useState(0);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    dispatch(getUserData())
      .then((userData) => {
        if (userData) {
          dispatch(set_user_auth(userData));
        }
        const queryParams = new URLSearchParams(window.location.search);
        const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
        const pId = pIdParam
          ? parseInt(pIdParam, 10)
          : (userData?.cleaning_profile_id || (userData?.profiles && userData?.profiles[0]?.ID) || 0);
        fetchProfile(pId);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        const queryParams = new URLSearchParams(window.location.search);
        const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
        fetchProfile(pIdParam ? parseInt(pIdParam, 10) : 0);
      });
  }, [location.search]);

  const fetchProfile = (targetId = 0) => {
    setLoading(true);
    dispatch(getCleaningRestorationProfileData(targetId))
      .then((res) => {
        const profileId = res?.profile_id || res?.id;
        if (res && (profileId || res.success)) {
          setProfileData(res);
          const pic = res.profile_pic || res.logo_url || "";
          setImageUrl(pic);
          setProfilePicId(res.profile_pic_id || res.thumbnail_id || 0);

          let servicesVal = [];
          if (Array.isArray(res.cleaning_restoration_services)) {
            servicesVal = res.cleaning_restoration_services;
          } else if (typeof res.cleaning_restoration_services === "string" && res.cleaning_restoration_services.length > 0) {
            servicesVal = res.cleaning_restoration_services.split(",").map((s) => s.trim()).filter(Boolean);
          }

          form.setFieldsValue({
            organization_name: res.organization_name || res.title || "",
            name: res.name || res.first_name || "",
            phone_number: res.phone_number || "",
            email: res.email || "",
            address: res.address || "",
            website_url: res.website_url || "",
            years_in_industry: res.years_in_industry || "",
            service_radius: res.service_radius || "50_miles",
            cleaning_restoration_services: servicesVal,
            iicrc_certified_firm: (res.iicrc_certified_firm === "1" || res.iicrc_certified_firm === 1) ? "1" : "0",
            iicrc_cleaning_certifications: Array.isArray(res.iicrc_cleaning_certifications) ? res.iicrc_cleaning_certifications : [],
            iicrc_restoration_certifications: Array.isArray(res.iicrc_restoration_certifications) ? res.iicrc_restoration_certifications : [],
            iicrc_advanced_designations: Array.isArray(res.iicrc_advanced_designations) ? res.iicrc_advanced_designations : [],
            iicrc_disclaimer_agreed: (res.iicrc_disclaimer_agreed === "1" || res.iicrc_disclaimer_agreed === 1 || res.iicrc_disclaimer_agreed === true),
          });
        }
      })
      .catch((err) => {
        console.error("Error loading Cleaning & Restoration profile:", err);
        message.error("Failed to load Cleaning & Restoration profile data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const beforeUploadImage = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!isJpgOrPng) {
      message.error("Please upload a JPG, PNG, or WEBP image file.");
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image file size must be less than 5MB.");
      return false;
    }
    return true;
  };

  const handleAvatarUpload = async (file) => {
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", file.name);

    try {
      const res = await axios.post(MEDIA_UPLOAD_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      });
      const resp = res.data;
      const attId = typeof resp === "object" && resp !== null ? resp.id : parseInt(resp, 10);
      const url =
        typeof resp === "object" && resp !== null && (resp.source_url || resp.url)
          ? resp.source_url || resp.url
          : URL.createObjectURL(file);

      if (attId) {
        setProfilePicId(attId);
        setImageUrl(url);
        message.success("Profile photo uploaded successfully!");
      }
    } catch (err) {
      console.error("Avatar upload failed:", err);
      message.error("Failed to upload profile photo.");
    } finally {
      setUploadingAvatar(false);
    }

    return false;
  };

  const handleFinish = (values) => {
    setSaving(true);
    const queryParams = new URLSearchParams(window.location.search);
    const pIdParam = queryParams.get("profile_id") || queryParams.get("cleaning_id") || queryParams.get("id");
    const activeProfileId =
      profileData?.id ||
      profileData?.profile_id ||
      (pIdParam ? parseInt(pIdParam, 10) : currentUser?.cleaning_profile_id || 0);

    // Merge form.getFieldsValue(true) with values so unmounted or inactive tab fields are ALWAYS preserved!
    const allFormValues = { ...form.getFieldsValue(true), ...values };

    const getSafeArray = (key) => {
      const val = allFormValues[key];
      if (Array.isArray(val)) return val;
      if (Array.isArray(profileData?.[key])) return profileData[key];
      return [];
    };

    const getSafeDisclaimer = () => {
      const val = allFormValues.iicrc_disclaimer_agreed;
      if (val === true || val === "1" || val === 1) return "1";
      if (val === false || val === "0" || val === 0) return "0";
      return (profileData?.iicrc_disclaimer_agreed === "1" || profileData?.iicrc_disclaimer_agreed === true || profileData?.iicrc_disclaimer_agreed === 1) ? "1" : "0";
    };

    const payload = {
      ...allFormValues,
      iicrc_certified_firm:
        (allFormValues.iicrc_certified_firm === "1" ||
          allFormValues.iicrc_certified_firm === true ||
          allFormValues.iicrc_certified_firm === 1)
          ? "1"
          : "0",
      cleaning_restoration_services: getSafeArray("cleaning_restoration_services"),
      iicrc_cleaning_certifications: getSafeArray("iicrc_cleaning_certifications"),
      iicrc_restoration_certifications: getSafeArray("iicrc_restoration_certifications"),
      iicrc_advanced_designations: getSafeArray("iicrc_advanced_designations"),
      iicrc_disclaimer_agreed: getSafeDisclaimer(),
      profile_id: activeProfileId,
      id: activeProfileId,
      profile_pic_id: profilePicId || 0,
      profile_pic: imageUrl || "",
    };

    dispatch(updateCleaningRestorationProfile(payload))
      .then((res) => {
        const pId = res?.id || res?.profile_id;
        if (res && (pId || res.success)) {
          setProfileData(res);
          if (res.profile_pic) setImageUrl(res.profile_pic);

          let servicesVal = [];
          if (Array.isArray(res.cleaning_restoration_services)) {
            servicesVal = res.cleaning_restoration_services;
          } else if (typeof res.cleaning_restoration_services === "string" && res.cleaning_restoration_services.length > 0) {
            servicesVal = res.cleaning_restoration_services.split(",").map((s) => s.trim()).filter(Boolean);
          }

          form.setFieldsValue({
            organization_name: res.organization_name || res.title || "",
            name: res.name || res.first_name || "",
            phone_number: res.phone_number || "",
            email: res.email || "",
            address: res.address || "",
            website_url: res.website_url || "",
            years_in_industry: res.years_in_industry || "",
            service_radius: res.service_radius || "50_miles",
            cleaning_restoration_services: servicesVal,
            iicrc_certified_firm: (res.iicrc_certified_firm === "1" || res.iicrc_certified_firm === 1) ? "1" : "0",
            iicrc_cleaning_certifications: Array.isArray(res.iicrc_cleaning_certifications) ? res.iicrc_cleaning_certifications : [],
            iicrc_restoration_certifications: Array.isArray(res.iicrc_restoration_certifications) ? res.iicrc_restoration_certifications : [],
            iicrc_advanced_designations: Array.isArray(res.iicrc_advanced_designations) ? res.iicrc_advanced_designations : [],
            iicrc_disclaimer_agreed: (res.iicrc_disclaimer_agreed === "1" || res.iicrc_disclaimer_agreed === 1 || res.iicrc_disclaimer_agreed === true),
          });

          message.success("Cleaning & Restoration profile updated successfully!");
          dispatch(getUserData())
            .then((userData) => {
              if (userData) dispatch(set_user_auth(userData));
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error("Save error:", err);
        message.error("Failed to update profile. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading) {
    return (
      <div className="inspectionWrapper" style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading Cleaning & Restoration profile..." />
      </div>
    );
  }

  // Strict boolean check for paid status (handles strings '0', '1', booleans, numbers)
  const checkIsPaid = (val) => {
    if (val === true || val === 1 || val === "1") return true;
    if (typeof val === "string" && val.toLowerCase() === "true") return true;
    return false;
  };

  const isExpired = Boolean(
    currentUser?.expired ||
    profileData?.expired ||
    currentUser?.user_disabled === "1" ||
    currentUser?.user_disabled === 1
  );

  const isPaid =
    !isExpired &&
    (checkIsPaid(currentUser?.is_active_paid) ||
      checkIsPaid(profileData?.is_active_paid) ||
      (Number(currentUser?.membership_id || profileData?.membership_id || 0) === 29 &&
        currentUser?.is_active_paid !== false &&
        currentUser?.is_active_paid !== "0" &&
        currentUser?.is_active_paid !== 0));

  const permalink = profileData?.permalink || "";
  const checkoutUrl = `${WEBSITE_URL}/membership-account/membership-checkout/?level=29`;

  const ProfileuploadButton = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <PlusOutlined style={{ fontSize: 20, color: "#334155", marginBottom: 6 }} />
      <span style={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
        Upload Profile
      </span>
    </div>
  );

  return (
    <div className="inspectionWrapper trusted-dealer-dashboard-wrapper">
      <div className="inspectionHeader">
        <PagesHeader title="Cleaning & Restoration Profile" />
      </div>

      <div className="buttonSection" style={{ position: "relative", zIndex: 100 }}>
        <div className="container-xxl">
          <ButtonBar isPaid={isPaid} />
        </div>
      </div>

      <div className="container-xxl inspector-profile-container" style={{ padding: "20px 0" }}>
        <Row justify="center">
          <Col xs={24} md={20}>
            {/* Dynamic Status Banners */}
            {!isPaid && (
              <Alert
                message="⚠️ Membership Inactive / Checkout Required"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13.5, color: "#7f1d1d", lineHeight: 1.6 }}>
                      Your Cleaning & Restoration membership is currently inactive or cancelled. To activate your public directory listing, unlock{" "}
                      <a
                        href={`${WEBSITE_URL}/defects/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontWeight: 700,
                          textDecoration: "underline",
                          color: "#0a497b",
                        }}
                      >
                        Claims & Conditions
                      </a>
                      , and use your Mobile Photo App seat, please complete checkout.
                    </span>
                    <Button
                      type="primary"
                      danger
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ borderRadius: 6, fontWeight: 700, height: 38 }}
                    >
                      Complete Plan Checkout &rarr;
                    </Button>
                  </div>
                }
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined style={{ fontSize: 24 }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16, background: "#fff5f5", borderColor: "#fecaca" }}
              />
            )}

            {isPaid && (
              <Alert
                message="🟢 Cleaning & Restoration Membership Active"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13.5, color: "#065f46" }}>
                      Your membership is active with 1 Mobile FD Photo App Seat included. Your company listing is live on the public directory.
                    </span>
                  </div>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined style={{ fontSize: 24, color: "#10b981" }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16, background: "#f0fdf4", borderColor: "#bbf7d0" }}
              />
            )}

            {/* EXACT INSPECTOR / CONSULTANT PROFILE FORM ARCHITECTURE */}
            <Form
              form={form}
              id="editProfileForm"
              name="editProfileForm"
              layout="vertical"
              preserve={true}
              onFinish={handleFinish}
            >
              {/* TOP ROW: AVATAR (LEFT) & STATUS BADGES (RIGHT) */}
              <Row gutter={16} align="middle" justify="space-between">
                <Col xs={24} sm={10} md={8}>
                  <div className="avatar-wrapper">
                    <Spin spinning={uploadingAvatar}>
                      <Upload
                        className="profile-image-upload"
                        showUploadList={false}
                        beforeUpload={beforeUploadImage}
                        customRequest={({ file }) => handleAvatarUpload(file)}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="avatar"
                            className="profileImage-Box"
                            style={{ width: "100%" }}
                          />
                        ) : (
                          ProfileuploadButton
                        )}

                        <Tooltip title="Upload Profile Picture">
                          <div className="upload-button">
                            <CloudUploadOutlined />
                          </div>
                        </Tooltip>
                      </Upload>
                    </Spin>
                  </div>
                </Col>

                {/* STATUS BADGES: PAID / UNPAID */}
                <Col xs={24} sm={14} md={16}>
                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 20
                  }}>
                    {isPaid ? (
                      <Tag
                        color="success"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(34, 197, 94, 0.2)",
                          margin: 0
                        }}
                      >
                        <CheckCircleFilled style={{ color: "#16a34a" }} />
                        Paid Plan Active
                      </Tag>
                    ) : (
                      <Tag
                        color="error"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(239, 68, 68, 0.2)",
                          margin: 0
                        }}
                      >
                        <ExclamationCircleOutlined style={{ color: "#dc2626" }} />
                        Unpaid / Checkout Required
                      </Tag>
                    )}

                    {permalink && (
                      <Button
                        type="default"
                        icon={<EyeOutlined />}
                        href={permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ borderRadius: 6, fontWeight: 600 }}
                      >
                        View Public Listing
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {/* BASIC DETAILS (3-COLUMN EXACT INSPECTOR PROFILE GRID) */}
              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Organization / Company Name"
                    name="organization_name"
                    rules={[{ required: true, message: "Please enter organization name!" }]}
                  >
                    <Input
                      prefix={<ShopOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Organization Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Contact Person Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter contact name!" }]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Contact Person Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Office / Service Address (Map Locator)"
                    name="address"
                    rules={[{ required: true, message: "Please enter business address!" }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Business Address"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true, type: "email", message: "Please enter valid email!" }]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Phone Number" name="phone_number">
                    <Input
                      prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Phone Number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Website Link (optional)" name="website_url">
                    <Input
                      prefix={<GlobalOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Website URL"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12} xl={12}>
                  <Form.Item label="Years in Industry (optional)" name="years_in_industry">
                    <Input
                      prefix={<ClockCircleOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="e.g. 15 Years"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} xl={12}>
                  <Form.Item label="Service Radius Coverage" name="service_radius">
                    <Select
                      placeholder="Select service radius"
                      className="service-radius-select"
                      style={{ width: "100%" }}
                    >
                      {SERVICE_RADIUS_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* SERVICES & CERTIFICATIONS TABS */}
              <Tabs defaultActiveKey="1" destroyInactiveTabPane={false} className="profile-tab-section mt-3">
                {/* TAB 1: SERVICES OFFERED */}
                <TabPane forceRender={true} tab={<span><StarOutlined /> Services Offered</span>} key="1">
                  <div style={{ padding: "16px 0" }}>
                    <div className="cr-tab-header-banner">
                      <div>
                        <h3 className="cr-tab-header-title">
                          <AppstoreOutlined style={{ color: "#0a497b" }} />
                          Professional Services &amp; Capabilities
                        </h3>
                        <p className="cr-tab-header-desc">
                          Select all specialized services your company provides. These selections appear on your public directory profile and power directory customer search filters.
                        </p>
                      </div>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) =>
                          prev.cleaning_restoration_services !== curr.cleaning_restoration_services
                        }
                      >
                        {() => {
                          const s = form.getFieldValue("cleaning_restoration_services") || [];
                          return (
                            <div className="cr-header-badge">
                              <CheckCircleFilled style={{ color: s.length > 0 ? "#10b981" : "#94a3b8" }} />
                              <span>{s.length} of 27 Selected</span>
                            </div>
                          );
                        }}
                      </Form.Item>
                    </div>

                    <Form.Item name="cleaning_restoration_services" style={{ margin: 0 }}>
                      <Checkbox.Group style={{ width: "100%" }}>
                        {CLEANING_RESTORATION_SERVICES_CATEGORIES.map((cat) => (
                          <div key={cat.category} className="cr-domain-section">
                            <div className="cr-domain-header">
                              <div className="cr-domain-title-wrap">
                                <div className="cr-domain-icon-box">
                                  {cat.iconType === "textile" ? (
                                    <AppstoreOutlined />
                                  ) : cat.iconType === "hardsurface" ? (
                                    <ProjectOutlined />
                                  ) : (
                                    <ThunderboltOutlined />
                                  )}
                                </div>
                                <div>
                                  <h4 className="cr-domain-title">{cat.category}</h4>
                                  <p className="cr-domain-tagline">{cat.tagline}</p>
                                </div>
                              </div>

                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) =>
                                  prev.cleaning_restoration_services !== curr.cleaning_restoration_services
                                }
                              >
                                {() => {
                                  const s = form.getFieldValue("cleaning_restoration_services") || [];
                                  const count = cat.services.filter((srv) => s.includes(srv.value)).length;
                                  return (
                                    <span className={`cr-domain-count-badge ${count > 0 ? "active" : ""}`}>
                                      {count > 0 ? `${count} / ${cat.services.length} Selected` : `${cat.services.length} Services`}
                                    </span>
                                  );
                                }}
                              </Form.Item>
                            </div>
                            <Row gutter={[16, 6]}>
                              {cat.services.map((srv) => (
                                <Col xs={24} sm={12} md={8} key={srv.value}>
                                  <Checkbox value={srv.value} className="cr-luxury-card">
                                    <span>{srv.label}</span>
                                  </Checkbox>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ))}
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                </TabPane>

                {/* TAB 2: PROFESSIONAL CERTIFICATIONS (IICRC) */}
                <TabPane forceRender={true} tab={<span><SafetyCertificateOutlined /> Professional Certifications</span>} key="2">
                  <div style={{ padding: "16px 0" }}>
                    <div className="cr-tab-header-banner">
                      <div>
                        <h3 className="cr-tab-header-title">
                          <SafetyCertificateOutlined style={{ color: "#0a497b" }} />
                          Professional Certifications (IICRC)
                        </h3>
                        <p className="cr-tab-header-desc">
                          Designate company-level accreditation and technician qualifications. Verified certifications are showcased prominently on your listing to elevate trust and authority.
                        </p>
                      </div>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) =>
                          prev.iicrc_cleaning_certifications !== curr.iicrc_cleaning_certifications ||
                          prev.iicrc_restoration_certifications !== curr.iicrc_restoration_certifications ||
                          prev.iicrc_advanced_designations !== curr.iicrc_advanced_designations
                        }
                      >
                        {() => {
                          const c1 = form.getFieldValue("iicrc_cleaning_certifications") || [];
                          const c2 = form.getFieldValue("iicrc_restoration_certifications") || [];
                          const c3 = form.getFieldValue("iicrc_advanced_designations") || [];
                          const total = c1.length + c2.length + c3.length;
                          return (
                            <div className="cr-header-badge">
                              <SafetyCertificateOutlined style={{ color: total > 0 ? "#10b981" : "#94a3b8" }} />
                              <span>{total} Credentials Selected</span>
                            </div>
                          );
                        }}
                      </Form.Item>
                    </div>

                    {/* COMPANY LEVEL STATUS: IICRC CERTIFIED FIRM */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.iicrc_certified_firm !== curr.iicrc_certified_firm
                      }
                    >
                      {() => {
                        const isFirm = form.getFieldValue("iicrc_certified_firm") === "1";
                        return (
                          <div className={`cr-firm-luxury-hero ${isFirm ? "firm-active" : ""}`}>
                            <div className="cr-firm-hero-left">
                              <div className="cr-firm-emblem">
                                <SafetyCertificateOutlined />
                              </div>
                              <div>
                                <h4 className="cr-firm-title">
                                  IICRC Certified Firm{" "}
                                  <Tag
                                    color={isFirm ? "success" : "default"}
                                    style={{
                                      borderRadius: 12,
                                      fontWeight: 700,
                                      padding: "2px 10px",
                                      fontSize: 11.5,
                                      margin: 0,
                                    }}
                                  >
                                    {isFirm ? "✓ Active Accreditation" : "Not Certified"}
                                  </Tag>
                                </h4>
                                <p className="cr-firm-desc">
                                  Official company-level accreditation awarded by the IICRC. Featured with a prominent Certified Firm badge across the public directory and in search filters.
                                </p>
                              </div>
                            </div>

                            <div className="cr-pill-segmented-container">
                              <div className={`cr-pill-slider ${isFirm ? "pos-left" : "pos-right"}`} />
                              <button
                                type="button"
                                className={`cr-segmented-option ${isFirm ? "active" : ""}`}
                                onClick={() => form.setFieldsValue({ iicrc_certified_firm: "1" })}
                              >
                                <CheckCircleFilled className="cr-segmented-icon" />
                                <span>Yes, Certified Firm</span>
                              </button>
                              <button
                                type="button"
                                className={`cr-segmented-option ${!isFirm ? "active" : ""}`}
                                onClick={() => form.setFieldsValue({ iicrc_certified_firm: "0" })}
                              >
                                <CloseCircleOutlined className="cr-segmented-icon" />
                                <span>No, Not Certified</span>
                              </button>
                              <Form.Item name="iicrc_certified_firm" style={{ display: "none" }}>
                                <Input />
                              </Form.Item>
                            </div>
                          </div>
                        );
                      }}
                    </Form.Item>

                    {/* GROUP 1: CLEANING CERTIFICATIONS */}
                    <div className="cr-domain-section">
                      <div className="cr-domain-header">
                        <div className="cr-domain-title-wrap">
                          <div className="cr-domain-icon-box">
                            <SafetyCertificateOutlined />
                          </div>
                          <div>
                            <h4 className="cr-domain-title">Cleaning Certifications</h4>
                            <p className="cr-domain-tagline">Technician qualifications for carpet, upholstery, leather, stone, and specialized floor care</p>
                          </div>
                        </div>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) =>
                            prev.iicrc_cleaning_certifications !== curr.iicrc_cleaning_certifications
                          }
                        >
                          {() => {
                            const c = form.getFieldValue("iicrc_cleaning_certifications") || [];
                            return (
                              <span className={`cr-domain-count-badge ${c.length > 0 ? "active" : ""}`}>
                                {c.length > 0 ? `${c.length} / ${IICRC_CLEANING_CERTS.length} Selected` : `${IICRC_CLEANING_CERTS.length} Qualifications`}
                              </span>
                            );
                          }}
                        </Form.Item>
                      </div>
                      <Form.Item name="iicrc_cleaning_certifications" style={{ margin: 0 }}>
                        <Checkbox.Group style={{ width: "100%" }}>
                          <Row gutter={[16, 6]}>
                            {IICRC_CLEANING_CERTS.map((cert) => {
                              const parts = cert.label.split(" - ");
                              const code = parts[0];
                              const name = parts.slice(1).join(" - ");
                              return (
                                <Col xs={24} sm={12} md={8} key={cert.value}>
                                  <Checkbox value={cert.value} className="cr-luxury-card">
                                    <span className="cr-cert-code-tag">{code}</span>
                                    <span>{name}</span>
                                  </Checkbox>
                                </Col>
                              );
                            })}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>
                    </div>

                    {/* GROUP 2: RESTORATION CERTIFICATIONS */}
                    <div className="cr-domain-section">
                      <div className="cr-domain-header">
                        <div className="cr-domain-title-wrap">
                          <div className="cr-domain-icon-box">
                            <ThunderboltOutlined />
                          </div>
                          <div>
                            <h4 className="cr-domain-title">Restoration Certifications</h4>
                            <p className="cr-domain-tagline">Specialized qualifications in water extraction, structural drying, microbial remediation, fire, odor & trauma</p>
                          </div>
                        </div>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) =>
                            prev.iicrc_restoration_certifications !== curr.iicrc_restoration_certifications
                          }
                        >
                          {() => {
                            const c = form.getFieldValue("iicrc_restoration_certifications") || [];
                            return (
                              <span className={`cr-domain-count-badge ${c.length > 0 ? "active" : ""}`}>
                                {c.length > 0 ? `${c.length} / ${IICRC_RESTORATION_CERTS.length} Selected` : `${IICRC_RESTORATION_CERTS.length} Qualifications`}
                              </span>
                            );
                          }}
                        </Form.Item>
                      </div>
                      <Form.Item name="iicrc_restoration_certifications" style={{ margin: 0 }}>
                        <Checkbox.Group style={{ width: "100%" }}>
                          <Row gutter={[16, 6]}>
                            {IICRC_RESTORATION_CERTS.map((cert) => {
                              const parts = cert.label.split(" - ");
                              const code = parts[0];
                              const name = parts.slice(1).join(" - ");
                              return (
                                <Col xs={24} sm={12} md={8} key={cert.value}>
                                  <Checkbox value={cert.value} className="cr-luxury-card">
                                    <span className="cr-cert-code-tag">{code}</span>
                                    <span>{name}</span>
                                  </Checkbox>
                                </Col>
                              );
                            })}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>
                    </div>

                    {/* GROUP 3: ADVANCED IICRC DESIGNATIONS */}
                    <div className="cr-domain-section">
                      <div className="cr-domain-header">
                        <div className="cr-domain-title-wrap">
                          <div className="cr-domain-icon-box">
                            <CrownOutlined />
                          </div>
                          <div>
                            <h4 className="cr-domain-title">Advanced IICRC Master Designations</h4>
                            <p className="cr-domain-tagline">The highest tier of professional achievement and credentialing awarded by the IICRC</p>
                          </div>
                        </div>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) =>
                            prev.iicrc_advanced_designations !== curr.iicrc_advanced_designations
                          }
                        >
                          {() => {
                            const c = form.getFieldValue("iicrc_advanced_designations") || [];
                            return (
                              <span className={`cr-domain-count-badge ${c.length > 0 ? "active" : ""}`}>
                                {c.length > 0 ? `${c.length} / ${IICRC_ADVANCED_DESIGNATIONS.length} Selected` : `Master Tier (${IICRC_ADVANCED_DESIGNATIONS.length})`}
                              </span>
                            );
                          }}
                        </Form.Item>
                      </div>
                      <Form.Item name="iicrc_advanced_designations" style={{ margin: 0 }}>
                        <Checkbox.Group style={{ width: "100%" }}>
                          <Row gutter={[16, 6]}>
                            {IICRC_ADVANCED_DESIGNATIONS.map((cert) => {
                              const parts = cert.label.split(" - ");
                              const code = parts[0];
                              const name = parts.slice(1).join(" - ");
                              return (
                                <Col xs={24} sm={12} md={8} key={cert.value}>
                                  <Checkbox value={cert.value} className="cr-luxury-card cr-master-tier-card">
                                    <span className="cr-cert-code-tag">{code}</span>
                                    <span>{name}</span>
                                  </Checkbox>
                                </Col>
                              );
                            })}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>
                    </div>

                    {/* MANDATORY CERTIFICATION ACCURACY ACKNOWLEDGMENT */}
                    <div className="cr-attestation-card">
                      <div className="cr-attestation-title">
                        <SafetyCertificateOutlined style={{ color: "#0a497b", fontSize: 17 }} />
                        <span>Professional Certification &amp; Accuracy Attestation</span>
                      </div>
                      <Form.Item
                        name="iicrc_disclaimer_agreed"
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Checkbox style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", lineHeight: 1.5 }}>
                          I confirm that the professional certifications and qualifications listed on my profile are accurate and current. I understand that Floor Detective does not independently verify these credentials.
                        </Checkbox>
                      </Form.Item>
                    </div>
                  </div>
                </TabPane>
              </Tabs>

              {/* SAVE BUTTON */}
              <div style={{ textAlign: "right", marginTop: 28 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  size="large"
                  icon={<FileDoneOutlined />}
                  style={{
                    background: "#0a497b",
                    borderColor: "#0a497b",
                    minWidth: 200,
                    height: 46,
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 15,
                    boxShadow: "0 4px 14px rgba(10, 73, 123, 0.25)",
                  }}
                >
                  Save Profile Changes
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CleaningRestorationDashboard;
